import { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { authService } from '../../services/api';
import { Lock, Eye, EyeOff, Loader2, CheckCircle } from 'lucide-react';

/**
 * Pagina reset password
 */
function ResetPasswordPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Le password non coincidono');
      return;
    }

    if (password.length < 8) {
      setError('La password deve avere almeno 8 caratteri');
      return;
    }

    setIsLoading(true);

    try {
      await authService.resetPassword(token, password, confirmPassword);
      setIsSubmitted(true);

      // Redirect al login dopo 3 secondi
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          'Errore durante il reset. Il link potrebbe essere scaduto.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Messaggio di conferma
  if (isSubmitted) {
    return (
      <div className="bg-card rounded-lg border border-border shadow-sm p-4 sm:p-6 animate-fadeIn">
        <div className="text-center">
          <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="text-green-600 dark:text-green-400" size={28} />
          </div>
          <h2 className="text-xl sm:text-2xl font-bold text-foreground">Password reimpostata</h2>
          <p className="text-muted-foreground mt-2 text-sm sm:text-base">
            La tua password è stata aggiornata con successo.
          </p>
          <p className="text-xs sm:text-sm text-muted-foreground mt-4">
            Verrai reindirizzato alla pagina di login...
          </p>

          <Link
            to="/login"
            className="inline-flex items-center justify-center mt-6 py-2.5 px-5 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 active:bg-primary/80 min-h-[44px] touch-manipulation"
          >
            Vai al login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border shadow-sm p-4 sm:p-6 animate-fadeIn">
      <div className="text-center mb-4 sm:mb-6">
        <h2 className="text-xl sm:text-2xl font-bold text-foreground">Nuova password</h2>
        <p className="text-muted-foreground mt-2 text-sm sm:text-base">
          Inserisci la tua nuova password
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
            {error}
          </div>
        )}

        <div>
          <label htmlFor="password" className="block text-sm font-medium mb-2">
            Nuova password
          </label>
          <div className="relative">
            <Lock
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              size={20}
            />
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full pl-10 pr-12 py-3 sm:py-3.5 rounded-lg border border-input bg-background text-foreground text-base focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder="••••••••"
              required
              minLength={8}
              autoComplete="new-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground active:text-foreground p-1.5 min-h-[44px] min-w-[44px] flex items-center justify-center touch-manipulation"
              aria-label={showPassword ? 'Nascondi password' : 'Mostra password'}
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Minimo 8 caratteri, con maiuscola, minuscola e numero
          </p>
        </div>

        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium mb-2">
            Conferma password
          </label>
          <div className="relative">
            <Lock
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              size={20}
            />
            <input
              id="confirmPassword"
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full pl-10 pr-4 py-3 sm:py-3.5 rounded-lg border border-input bg-background text-foreground text-base focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder="••••••••"
              required
              autoComplete="new-password"
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="w-full py-3 sm:py-3.5 px-4 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 active:bg-primary/80 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 min-h-[48px] touch-manipulation"
        >
          {isLoading && <Loader2 className="animate-spin" size={20} />}
          Reimposta password
        </button>

        <Link
          to="/login"
          className="block text-center text-sm text-muted-foreground hover:text-foreground active:text-foreground py-2 touch-manipulation"
        >
          Torna al login
        </Link>
      </form>
    </div>
  );
}

export default ResetPasswordPage;
